// SPDX-License-Identifier: AGPL-3.0-or-later

import type {RuntimeConfigSnapshot} from '@app/features/app/state/RuntimeConfigSnapshot';
import {instanceRequest, instanceTargetFromSnapshot} from '@app/features/platform/transport/InstanceHTTP';

export interface PrivyWalletBinding {
	required: boolean;
	linked: boolean;
	wallet_address: string | null;
}
export interface PrivyLoginResponse {
	token: string;
	user_id: string;
	wallet_binding: PrivyWalletBinding;
}

export async function exchangePrivyLogin(
	snapshot: RuntimeConfigSnapshot,
	accessToken: string,
): Promise<PrivyLoginResponse> {
	const response = await instanceRequest<PrivyLoginResponse>({
		method: 'POST',
		path: '/auth/privy/login',
		target: instanceTargetFromSnapshot(snapshot),
		auth: 'none',
		retries: 0,
		body: {access_token: accessToken},
	});
	return response.body;
}
export async function confirmPrivyWallet(
	snapshot: RuntimeConfigSnapshot,
	accessToken: string,
	address: string,
	token?: string,
): Promise<PrivyWalletBinding> {
	const response = await instanceRequest<PrivyWalletBinding>({
		method: 'POST',
		path: '/auth/privy/wallet/confirm',
		target: instanceTargetFromSnapshot(snapshot),
		auth: token ? 'none' : 'session',
		retries: 0,
		...(token ? {headers: {Authorization: token}} : {}),
		body: {access_token: accessToken, wallet_address: address},
	});
	return response.body;
}

export interface PrivyReauthChallenge {
	challenge_id: string;
	masked_email: string;
	expires_at: string;
}
export async function startPrivyReauth(
	snapshot: RuntimeConfigSnapshot,
	accessToken: string,
	signal?: AbortSignal,
): Promise<PrivyReauthChallenge> {
	const response = await instanceRequest<PrivyReauthChallenge>({
		method: 'POST',
		path: '/auth/privy/reauth/start',
		target: instanceTargetFromSnapshot(snapshot),
		auth: 'session',
		retries: 0,
		body: {access_token: accessToken},
		signal,
	});
	return response.body;
}
export async function completePrivyReauth(
	snapshot: RuntimeConfigSnapshot,
	challengeId: string,
	code: string,
	signal?: AbortSignal,
): Promise<{sudo_token: string; expires_at: string}> {
	const response = await instanceRequest<{sudo_token: string; expires_at: string}>({
		method: 'POST',
		path: '/auth/privy/reauth/complete',
		target: instanceTargetFromSnapshot(snapshot),
		auth: 'session',
		retries: 0,
		body: {challenge_id: challengeId, code},
		signal,
	});
	return response.body;
}
